import type { DicomGroups, DicomSeries } from '@/lib/dicom/DicomSeries';
import { formatPatientName } from '@/lib/dicom/dicomPatientName';
import { formatImageSize } from '@/util/format/formatImageSize';
import { formatOptional } from '@/util/format/formatOptional';

/**
 * A flat, display-ready row for one series in the selection modal. Values are
 * read from the first file in the group and pre-formatted so the view stays
 * dumb.
 */
export interface SeriesRow {
    seriesInstanceUid: string;
    seriesNumber: string;
    seriesDescription: string;
    patientName: string;
    modality: string;
    fileCount: number;
    sliceThickness: string;
    bodyPartExamined: string;
    imageSize: string;
}

function toRow(seriesInstanceUid: string, series: DicomSeries): SeriesRow {
    const first = series.items[0];
    return {
        seriesInstanceUid,
        seriesNumber: formatOptional(first.seriesNumber),
        seriesDescription: formatOptional(first.seriesDescription),
        patientName: first.patientName ? formatPatientName(first.patientName) : '--',
        modality: formatOptional(first.modality),
        fileCount: series.items.length,
        sliceThickness: formatOptional(first.sliceThickness),
        bodyPartExamined: formatOptional(first.bodyPartExamined),
        imageSize: formatImageSize(first.rows, first.columns),
    };
}

/**
 * Project pre-screened {@link DicomGroups} into display rows, sorted by
 * SeriesInstanceUID for deterministic ordering in the picker.
 */
export function toSeriesRows(groups: DicomGroups): SeriesRow[] {
    return Object.entries(groups)
        .map(([uid, series]) => toRow(uid, series))
        .sort((a, b) => a.seriesInstanceUid.localeCompare(b.seriesInstanceUid));
}
